import {
  ArrowDownOutlined,
  FilterOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { GET_ME, Me, PERMISSIONS } from '@frontend/data-access';
import {
  IdeaChannelSettings,
  selectChannelFilters,
  selectIdeaSortby,
  setChannelFilters,
  setIdeaSortby,
} from '@frontend/feature-idea-channel';
import { Badge, Button, Dropdown, Input, Layout, Modal, Space } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../channel-store/settings/hooks';
import IdeaFilters from '../../idea-filters/idea-filters';
import DownloadIdeasButton from './download-ideas-button';

const { Header } = Layout;
const { Search } = Input;

type SortOptionType = 'ASC' | 'DESC';

function IdeaChannelHeader() {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { pathname, query } = router;
  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const dispatch = useAppDispatch();
  const channelFilters = useAppSelector(selectChannelFilters);
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const [isOpenFilters, setIsOpenFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>();
  const sortby = useAppSelector(selectIdeaSortby);
  const filters = useAppSelector(selectChannelFilters);
  const filtersLength = Object.values(filters || {}).filter(
    (item) => item?.length
  ).length;

  const handleSettings = () => {
    setIsOpenSettings(!isOpenSettings);
  };

  const handleFilters = () => {
    setIsOpenFilters(!isOpenFilters);
  };

  const handleSort = (value: SortOptionType) => {
    dispatch(setIdeaSortby(value));
  };

  const isAccessDownloadIdeas = useMemo(() => {
    if (!query['id']) return false;

    const role = meData?.me.role;
    const permissions = meData?.me.permissions ?? [];

    return (
      role === 'ADMIN' ||
      permissions.includes(
        `${PERMISSIONS.INTERNAL_CHALLENGE_MANAGEMENT}&FULL`
      ) ||
      permissions.includes(`${PERMISSIONS.CHANNEL_REVIEWER}&FULL`)
    );
  }, [meData?.me.permissions, meData?.me.role, query]);

  const isSortable = pathname === '/idea-hub';
  const isShowFilters =
    pathname === '/idea-hub' || pathname === '/idea-hub/evaluate';

  useEffect(() => {
    const WAIT_INTERVAL = 500;
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        dispatch(setChannelFilters({ ...channelFilters, name: searchTerm }));
      }
    }, WAIT_INTERVAL);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchTerm]);

  return (
    <Header
      style={{ paddingLeft: 16, paddingRight: 16, background: '#fff' }}
      className="border-b border-gray-400 flex items-center justify-between"
    >
      <div>
        {isShowFilters && (
          <div className="flex items-center gap-3">
            <Search
              defaultValue={channelFilters?.name}
              onChange={(e) => setSearchTerm(e.target.value.trim())}
              className="w-80"
              placeholder={t('search_idea_name')}
              allowClear
            />
            <Badge count={filtersLength} color="#0024A6">
              <Button
                type={filtersLength ? 'primary' : 'dashed'}
                shape="round"
                icon={<FilterOutlined />}
                onClick={handleFilters}
                ghost={!!filtersLength}
              >
                {filtersLength ? t('filters') : t('add_filters')}
              </Button>
            </Badge>
            {isSortable && (
              <div className="md:invisible lg:visible flex gap-2">
                <span className="text-nowrap">{t('sort_by_days')}:</span>
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      {
                        key: 'ASC',
                        label: t('newest'),
                        onClick: () => handleSort('ASC'),
                      },
                      {
                        key: 'DESC',
                        label: t('oldest'),
                        onClick: () => handleSort('DESC'),
                      },
                    ],
                  }}
                >
                  <div className="text-primary-600 cursor-pointer">
                    <Space>
                      <ArrowDownOutlined />
                      {sortby === 'ASC' ? t('newest') : t('oldest')}
                    </Space>
                  </div>
                </Dropdown>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {isAccessDownloadIdeas && <DownloadIdeasButton />}
        {meData?.me.role === 'ADMIN' && (
          <Button
            shape="round"
            icon={<SettingOutlined />}
            onClick={handleSettings}
            data-test="admin-settings-btn"
          >
            {t('admin_settings')}
          </Button>
        )}
      </div>
      <Modal
        title={t('filters')}
        open={isOpenFilters}
        width={728}
        onCancel={handleFilters}
        footer={null}
        centered
        forceRender
      >
        <IdeaFilters isOpen={isOpenFilters} handleClose={handleFilters} />
      </Modal>
      <Modal
        title={
          <div className="text-sm font-medium space-x-2">
            <SettingOutlined />
            <span className="">{t('settings')}</span>
          </div>
        }
        open={isOpenSettings}
        width={728}
        onCancel={handleSettings}
        centered
        footer={null}
      >
        <IdeaChannelSettings />
      </Modal>
    </Header>
  );
}

export default IdeaChannelHeader;
